import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, RmaRequest, WarrantyEntitlement } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-rma-requests',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('catalog.rma.requests.title')" [description]="t('catalog.rma.requests.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <section class="lookup-panel">
            <label>
              <span>{{ t('catalog.warranty.lookup') }}</span>
              <input pInputText type="text" [(ngModel)]="warrantyLookupRef" [placeholder]="t('catalog.warranty.lookupHint')" />
            </label>
            <button pButton type="button" [label]="t('catalog.warranty.checkEntitlement')" severity="secondary" [disabled]="checkingWarranty()" (click)="checkWarranty()"></button>
            @if (warrantyResult()) {
              <p class="warranty-result" [class.entitled]="warrantyResult()!.isEntitled">
                {{ warrantyResult()!.isEntitled ? t('catalog.warranty.entitled') : t('catalog.warranty.notEntitled') }}
                — {{ warrantyResult()!.matchingContracts.length }} {{ t('catalog.warranty.contractsFound') }}
              </p>
            }
          </section>

          <p-table [value]="rows()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.rma.requestNumber') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.rma.reasonCode') }}</th>
                <th>{{ t('catalog.warranty.underWarranty') }}</th>
                <th>{{ t('catalog.service.customerReference') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.requestNumber }}</button></td>
                <td>{{ row.status }}</td>
                <td>{{ row.reasonCode }}</td>
                <td>{{ row.isUnderWarranty ? t('common.yes') : t('common.no') }}</td>
                <td>{{ row.customerReferenceNumber || '-' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; }
    .lookup-panel { display: flex; flex-wrap: wrap; gap: 10px; align-items: end; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; }
    .lookup-panel label { display: grid; gap: 6px; min-width: 240px; flex: 1; }
    .lookup-panel span { font-size: 12px; color: #475569; font-weight: 600; }
    .warranty-result { margin: 0; font-size: 13px; color: #b91c1c; }
    .warranty-result.entitled { color: #15803d; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
  `]
})
export class RmaRequestsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly checkingWarranty = signal(false);
  readonly rows = signal<RmaRequest[]>([]);
  readonly warrantyResult = signal<WarrantyEntitlement | null>(null);

  warrantyLookupRef = '';

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service.listRmaRequests().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  checkWarranty(): void {
    const ref = this.warrantyLookupRef.trim();
    if (!ref) return;

    this.checkingWarranty.set(true);
    this.service.getWarrantyEntitlement(ref).subscribe({
      next: result => {
        this.warrantyResult.set(result);
        this.checkingWarranty.set(false);
      },
      error: () => {
        this.warrantyResult.set(null);
        this.checkingWarranty.set(false);
      }
    });
  }

  openDetail(id: string): void {
    void this.router.navigate(['/services/rma', id]);
  }

  openCreate(): void {
    void this.router.navigate(['/services/rma', 'new']);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
